import { ArticleType, Category } from '@wix/answers-api';
import { Badge, Box, Tag } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FunctionComponent } from 'react';

import { searchResultItemBadgeDataHooks } from './dataHooks';
import css from './styles.scss';

export type SearchResultItemBadgesProps = {
  articleType: ArticleType;
  rootTopic?: Category;
  onArticleTopicBadgeClick: (rootTopic: Category) => void;
  onArticleTypeTagClick: (clickedText: string, url: string) => void;
  LinkComponent: React.ComponentType<any>;
  badgeLinkProps?: Record<string, any>;
};

export const SearchResultItemBadges: FunctionComponent<SearchResultItemBadgesProps> = ({
  rootTopic,
  articleType,
  onArticleTopicBadgeClick,
  LinkComponent,
  badgeLinkProps,
}) => {
  const { t } = useTranslation();

  const getArticleTypeTitle = (): string => {
    switch (articleType) {
      case ArticleType.FEATURE_REQUEST:
        return t('feature.request.title');
      case ArticleType.KNOWN_ISSUE:
        return t('known.issue.title');
      case ArticleType.ARTICLE:
      default:
        return t('article.title');
    }
  };

  return (
    <Box
      className={css.searchResultItemBadges}
      verticalAlign="middle"
      dataHook={searchResultItemBadgeDataHooks.searchResultItemBadges()}
      gap="SP2"
    >
      {rootTopic ? (
        <LinkComponent
          onClick={() => onArticleTopicBadgeClick(rootTopic)}
          className={css.topicBadgeLink}
          {...badgeLinkProps}
        >
          <div tabIndex={0}>
            <Badge
              size="tiny"
              skin="neutralLight"
              dataHook={searchResultItemBadgeDataHooks.searchResultItemTopicBadge()}
              className={css.topicBadge}
            >
              {rootTopic.name}
            </Badge>
          </div>
        </LinkComponent>
      ) : null}
      <div tabIndex={0}>
        <Tag
          id={`${articleType}`}
          size="tiny"
          className={css.typeTag}
          removable={false}
          theme="lightOutlined"
          dataHook={searchResultItemBadgeDataHooks.searchResultItemTypeBadge()}
        >
          {getArticleTypeTitle()}
        </Tag>
      </div>
      <div className={css.scrollableIndicator} />
    </Box>
  );
};
